// The repeat warning for choir songs: a song that was sung – or is planned –
// close to a service gets a hint at the input field. Pure; all dates are
// YYYY-MM-DD strings.

export const DEFAULT_REPEAT_WARNING_WEEKS = 8;

export type SongRepeat = {
    /** The other performance that is too close. */
    date: string;
    /** True when it lies after the service (already planned), false when before. */
    planned: boolean;
};

function daysBetween(a: string, b: string): number {
    return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/**
 * The performance of a song that is closer than `weeks` weeks to a service on
 * `serviceDate`, or null. The service's own day never counts. Of several, the
 * last one before the service wins ("zuletzt am …"); only if there is none,
 * the next planned one after it.
 *
 * @param otherDates every other known date of the song, in any order
 * @param weeks      0 switches the warning off
 */
export function findRepeat(serviceDate: string, otherDates: readonly string[], weeks: number): SongRepeat | null {
    if (!serviceDate || !(weeks > 0)) return null;
    const maxDays = weeks * 7;
    let before: string | null = null;
    let after: string | null = null;
    for (const date of otherDates) {
        if (!date || date === serviceDate) continue;
        const distance = daysBetween(date, serviceDate);
        if (Number.isNaN(distance) || Math.abs(distance) > maxDays) continue;
        if (distance > 0 && (!before || date > before)) before = date;
        if (distance < 0 && (!after || date < after)) after = date;
    }
    if (before) return { date: before, planned: false };
    return after ? { date: after, planned: true } : null;
}

/** "14.09." – within the same year as `serviceDate` – or "14.09.2025". */
export function formatRepeatDate(date: string, serviceDate: string): string {
    const [year, month, day] = date.split("-");
    return year === serviceDate.slice(0, 4) ? `${day}.${month}.` : `${day}.${month}.${year}`;
}
